import { Tag } from 'antd';
import { Link } from 'react-router';
import { unitPath } from '../../fleet/features/units/units.paths';
import { jobPath } from '../../jobs/jobs.paths';
import { deadlineRows, type DeadlineRow } from '../policies/deadline-rows.policy';
import { stopRows, type TripStop } from '../policies/trip-stops.policy';
import { tripStatusLabel, tripStatusTone, type TripStatusTone } from '../policies/trip-status-label.policy';
import type { TripEta, TripPageData } from '../trips.api';

const STATUS_TAG_COLOURS: Record<TripStatusTone, string> = { neutral: 'default', active: 'processing', done: 'success', trouble: 'error' };
const DEADLINE_TEXT_CLASSES: Record<DeadlineRow['tone'], string> = { ok: 'text-success', risk: 'text-error', neutral: 'text-muted' };
const TRIP_OUT_STATUSES = ['dispatched', 'in_transit', 'exception'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="truncate text-sm">{children}</dd>
    </div>
  );
}

/** The state, the job and customer it serves, the crew, and the deadlines with how much slack the latest estimate leaves. */
export function TripHeader({ trip, eta, stops }: { trip: TripPageData; eta: TripEta | null; stops: TripStop[] }) {
  const deadlines = [...deadlineRows(eta, TRIP_OUT_STATUSES.includes(trip.status)), ...stopRows(stops)];

  return (
    <section aria-label="Trip summary" className="rounded-lg border border-line bg-surface p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Tag color={STATUS_TAG_COLOURS[tripStatusTone(trip.status)]}>{tripStatusLabel(trip.status)}</Tag>
        <span className="text-sm text-muted">{tripStatusLabel(trip.legType)} leg</span>
      </div>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Job">
          <Link to={jobPath(trip.job.id)}>{trip.job.number}</Link>
        </Field>
        <Field label="Customer">{trip.customerName ?? '—'}</Field>
        <Field label="Route">{trip.originZoneCode && trip.destinationZoneCode ? `${trip.originZoneCode} → ${trip.destinationZoneCode}` : '—'}</Field>
        <Field label="Truck">{trip.truck ? <Link to={unitPath(trip.truck.registration)}>{trip.truck.registration}</Link> : '—'}</Field>
        <Field label="Trailer">{trip.trailer ? <Link to={unitPath(trip.trailer.registration)}>{trip.trailer.registration}</Link> : '—'}</Field>
        <Field label="Driver">{trip.driver?.name ?? '—'}</Field>
      </dl>
      {deadlines.length > 0 && (
        <ul aria-label="Deadlines" className="mt-3 flex flex-col gap-1 border-t border-line pt-3 text-sm">
          {deadlines.map((row) => (
            <li key={`${row.label}-${row.text}`}>
              <span className="text-muted">{row.label}: </span>
              <span className={DEADLINE_TEXT_CLASSES[row.tone]}>{row.text}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
